import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import ConceptModelDiagram from 'features/taskManagement/ConceptModelDiagram'

const ITEMS = [
  {
    label: 'Obligation',
    tint: '#1f8c97',
    text: 'the requirement. A recurring legal or contractual duty that applies to a building. For example a valid EICR on file, or an annual fire-door inspection. It is what your compliance score measures, and it is met by current evidence or completed work.',
  },
  {
    label: 'Task',
    tint: '#33becc',
    text: 'the planned work. Scheduled, often recurring work you carry out and record to keep an obligation met. For example completing the monthly lift service, or filling in an inspection form. Completing a task on time is what keeps the matching obligation satisfied. Every task has a type: corrective (created from a remedial), obligation (keeps an obligation met), or one-off and recurring (other planned work). You can filter tasks and reports by type.',
  },
  {
    label: 'Remedial',
    tint: '#D24724',
    text: 'a problem to put right. A deficiency found, an overdue obligation, or an issue picked up from a document, incident or inspection. You resolve it directly, or turn it into a task to do the corrective work (completing that task closes the remedial).',
  },
]

const TasksRemedialsExplainer = ({ label = 'What’s the difference?' }) => {
  const [open, setOpen] = useState(false)

  useEffect(() => {
    if (!open) return
    const onKey = e => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [open])

  return (
    <Wrap>
      <InfoButton
        type='button'
        aria-label='What’s the difference between obligations, tasks and remedials?'
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <InfoOutlinedIcon style={{ fontSize: 15 }} />
        <span>{label}</span>
      </InfoButton>

      {open && createPortal(
        <Backdrop
          onMouseDown={e => { if (e.target === e.currentTarget) setOpen(false) }}
        >
          <Dialog role='dialog' aria-modal='true' aria-label='Obligations, tasks and remedials'>
            <Header>
              <HeaderTitle>Obligations, tasks &amp; remedials</HeaderTitle>
              <CloseBtn type='button' aria-label='Close' onClick={() => setOpen(false)}>
                <CloseRoundedIcon style={{ fontSize: 18 }} />
              </CloseBtn>
            </Header>

            <Body>
              <Lead>
                An <strong>obligation</strong> is what a building must do; a <strong>task</strong> is the
                work that meets it; a <strong>remedial</strong> is a problem to put right. Several things
                can raise a remedial. And both obligations and remedials are closed by doing a task.
              </Lead>

              <ConceptModelDiagram />

              <List>
                {ITEMS.map(i => (
                  <Row key={i.label}>
                    <Dot $colour={i.tint} />
                    <Text><strong>{i.label}</strong>: {i.text}</Text>
                  </Row>
                ))}
              </List>
            </Body>
          </Dialog>
        </Backdrop>,
        document.body
      )}
    </Wrap>
  )
}

export default TasksRemedialsExplainer

const Wrap = styled.span`
  display: inline-flex;
`

const InfoButton = styled.button`
  font: inherit;
  display: inline-flex; align-items: center; gap: 5px;
  background: rgba(255, 255, 255, 0.10);
  border: 1px solid rgba(12, 18, 41, 0.12);
  color: inherit;
  border-radius: 999px;
  padding: 4px 11px 4px 8px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 160ms ease;
  &:hover { background: rgba(12, 18, 41, 0.06); }
`

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100100;
  background: rgba(12, 18, 41, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`

const Dialog = styled.div`
  background: #fff;
  border-radius: 16px;
  width: 880px;
  max-width: 100%;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(12, 18, 41, 0.28);
`

const Header = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f2;
`

const HeaderTitle = styled.h4`
  margin: 0;
  font-size: 16px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
`

const CloseBtn = styled.button`
  font: inherit;
  flex-shrink: 0;
  display: inline-flex; align-items: center; justify-content: center;
  width: 30px; height: 30px;
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.grey[800]};
  border-radius: 8px;
  cursor: pointer;
  opacity: 0.7;
  &:hover { opacity: 1; background: rgba(12, 18, 41, 0.05); }
`

const Body = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  padding: 18px 20px 22px;
`

const Lead = styled.p`
  margin: 0 0 16px;
  font-size: 13px;
  line-height: 1.5;
  color: #4d4d56;
  strong { color: ${({ theme }) => theme.colors.haiti}; }
`

const List = styled.div`
  display: flex; flex-direction: column; gap: 11px;
  margin-top: 18px;
`

const Row = styled.div`
  display: flex; gap: 9px; align-items: flex-start;
`

const Dot = styled.span`
  flex: 0 0 auto;
  width: 9px; height: 9px; margin-top: 5px;
  border-radius: 50%;
  background: ${({ $colour }) => $colour};
  box-shadow: 0 0 0 2px ${({ $colour }) => `${$colour}33`};
`

const Text = styled.span`
  font-size: 12.5px; line-height: 1.5; color: #4d4d56;
  strong { color: ${({ theme }) => theme.colors.haiti}; }
`
